/**
 * Wrap Native Token Hook
 *
 * Handles wrapping native tokens (e.g., HYPE to WHYPE).
 * Used for eEarn deposits when depositing native HYPE.
 */

import { useCallback, useEffect, useState } from 'react'
import { useAccount, useWriteContract } from 'wagmi'
import { useWaitForTransactionReceiptUniversal } from '@/domain/hooks/useWaitForTransactionReceiptUniversal'
import { useQueryClient } from '@tanstack/react-query'
import { Address } from 'viem'
import { WHYPE_ADDRESS_HYPEREVM } from '@/domain/euler/nativeHype'

export interface UseWrapNativeTokenParams {
  /** Amount to wrap (in wei) */
  amount: bigint
  /** Chain ID */
  chainId: number
  /** Whether the hook is enabled */
  enabled?: boolean
  /** Custom wrapped token address (defaults to WHYPE) */
  wrappedTokenAddress?: Address
}

export type WrapStep = 'idle' | 'wrapping' | 'wrapped'

export interface UseWrapNativeTokenResult {
  /** Current wrap step */
  step: WrapStep
  /** Whether wrapping is loading/pending */
  isLoading: boolean
  /** Wrap function */
  wrap: () => void
  /** Reset the wrap state */
  reset: () => void
  /** Any error that occurred */
  error: Error | null
  /** Transaction hash */
  hash?: `0x${string}`
}

// WETH/WHYPE deposit ABI
const wrapAbi = [
  {
    name: 'deposit',
    type: 'function',
    stateMutability: 'payable',
    inputs: [],
    outputs: [],
  },
] as const

export function useWrapNativeToken({
  amount,
  chainId,
  enabled = true,
  wrappedTokenAddress = WHYPE_ADDRESS_HYPEREVM,
}: UseWrapNativeTokenParams): UseWrapNativeTokenResult {
  const { address } = useAccount()
  const queryClient = useQueryClient()
  const [step, setStep] = useState<WrapStep>('idle')

  // Wrap transaction
  const {
    writeContract,
    data: wrapHash,
    isPending: isWrapPending,
    error: wrapError,
    reset: resetWrite,
  } = useWriteContract()

  const { isLoading: isWrapConfirming, isSuccess: isWrapSuccess } = useWaitForTransactionReceiptUniversal({
    hash: wrapHash,
    chainId,
  })

  // Update step when wrap transaction confirms
  useEffect(() => {
    if (isWrapSuccess && step === 'wrapping') {
      setStep('wrapped')

      // Invalidate wallet balances after wrap completes (native HYPE -> WHYPE)
      if (address) {
        void queryClient.invalidateQueries({
          queryKey: ['walletTokenBalances', chainId, address],
        })
        void queryClient.invalidateQueries({
          queryKey: ['readContract'],
        })
        void queryClient.invalidateQueries({
          queryKey: ['readContracts'],
        })
      }
    }
  }, [isWrapSuccess, step, address, chainId, queryClient])

  // Reset step if user rejects or error occurs
  useEffect(() => {
    if (wrapError && step === 'wrapping') {
      setStep('idle')
      resetWrite()
    }
  }, [wrapError, step, resetWrite])

  // Wrap function
  const wrap = useCallback(() => {
    if (!address || !enabled || amount === 0n) return
    setStep('wrapping')

    writeContract({
      address: wrappedTokenAddress,
      abi: wrapAbi,
      functionName: 'deposit',
      value: amount,
      chainId,
    })
  }, [address, enabled, amount, wrappedTokenAddress, writeContract, chainId])

  // Reset function
  const reset = useCallback(() => {
    setStep('idle')
    resetWrite()
  }, [resetWrite])

  const isLoading = isWrapPending || isWrapConfirming || step === 'wrapping'

  return {
    step,
    isLoading,
    wrap,
    reset,
    error: wrapError as Error | null,
    hash: wrapHash,
  }
}
